/**
 * 檔案用途區塊
 * @module adapter-factory
 * @purpose 管理 WalletConnect 所需 adapter 的建立與網路型別判斷，集中處理 wagmi 建構條件與多 adapter 產生流程。
 * @external https://innotech.atlassian.net/browse/IN-116428
 * @external https://innotech.atlassian.net/browse/IN-111892
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
import { AppKitNetwork } from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import type { AdapterType } from '@modules/walletconnect/config/token-network-adapter-mapping';

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 adapterType 建立對應 adapter；目前 wagmi 需至少一個 network，其他未實作鏈別直接拋錯。
 * @purpose 統一單一 adapter 的建構規則，避免外部重複處理初始化前置檢查。
 * @external https://innotech.atlassian.net/browse/IN-116428
 */
export function createAdapter(adapterType: AdapterType, networks: AppKitNetwork[], projectId: string): WagmiAdapter {
  switch (adapterType) {
    case 'wagmi': {
      // WagmiAdapter 需要 networks
      if (networks.length === 0) {
        throw new Error('[AdapterFactory] WagmiAdapter requires at least one network');
      }
      return new WagmiAdapter({
        networks: networks as [AppKitNetwork, ...AppKitNetwork[]],
        projectId,
        ssr: false,
      });
    }

    case 'solana': {
      // TODO: 未來實作 SolanaAdapter
      // 目前公司未使用 Solana，暫時拋出錯誤
      throw new Error('[AdapterFactory] SolanaAdapter not implemented');
    }

    case 'ton': {
      // TODO: 未來實作 TonAdapter
      // 目前公司未使用 TON，暫時拋出錯誤
      throw new Error('[AdapterFactory] TonAdapter not implemented');
    }

    default: {
      // TypeScript 會確保所有 case 都被處理
      const exhaustiveCheck: never = adapterType;
      throw new Error(`[AdapterFactory] Unsupported adapter type: ${String(exhaustiveCheck)}`);
    }
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依輸入的 adapterTypes 與對應 network 清單批次建立 adapters；若單一 adapter 失敗則略過並持續處理。
 * @purpose 在多鏈情境下維持建立流程的容錯性，避免單一鏈別設定問題中斷整體初始化。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function createAdapters(
  adapterTypes: AdapterType[],
  networksByAdapterType: Map<AdapterType, AppKitNetwork[]>,
  projectId: string,
): WagmiAdapter[] {
  const adapters: WagmiAdapter[] = [];

  adapterTypes.forEach((adapterType) => {
    try {
      const networks = networksByAdapterType.get(adapterType);

      // 對於 wagmi adapter，必須有 networks
      if (adapterType === 'wagmi') {
        if (!networks || networks.length === 0) {
          return; // 跳過這個 adapter，不建立
        }
      }

      // 對於其他 adapter（如 bitcoin），可能不需要 networks
      const finalNetworks = networks || [];
      const adapter = createAdapter(adapterType, finalNetworks, projectId);
      adapters.push(adapter);
    } catch {
      // 繼續處理其他 adapters，不中斷整個流程
    }
  });

  return adapters;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 透過 caipNetworkId 是否以 bip122: 開頭判定輸入 network 是否屬於 Bitcoin 類型。
 * @purpose 提供 Bitcoin 與其他鏈別的快速辨識條件，供 adapter 或流程分支判斷使用。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function isBitcoinNetwork(network: AppKitNetwork): boolean {
  // 使用 caipNetworkId 判斷，Bitcoin 的格式是 bip122:...
  const { caipNetworkId } = network as { caipNetworkId?: string };
  return caipNetworkId?.startsWith('bip122:') === true;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 先以 caipNetworkId 的 eip155: 前綴判定 EVM；若缺少該欄位則退回以 id 是否為數字作為輔助判斷。
 * @purpose 提供 EVM network 的相容性辨識方式，降低不同 network 物件格式差異造成的判斷失準。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function isEVMNetwork(network: AppKitNetwork): boolean {
  const networkWithId = network as { caipNetworkId?: string; id?: number | string };

  // 方法 1: 如果有 caipNetworkId，直接檢查是否以 eip155: 開頭
  if (networkWithId.caipNetworkId) {
    return networkWithId.caipNetworkId.startsWith('eip155:');
  }

  // 方法 2: 如果沒有 caipNetworkId，檢查 id 是否為數字
  // EVM 鏈的 id 都是數字（如 1, 56, 137），而 Bitcoin 的 id 是字串
  if (networkWithId.id !== undefined) {
    return typeof networkWithId.id === 'number';
  }

  // 預設：無法判斷，回傳 false
  return false;
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:20:15.638Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note 已完成註解重構：新增檔案頂部「檔案用途區塊」、為各 exported function 補齊「宣告內容用途說明與單號關聯」JSDoc（含對應 Jira 外部連結），並在檔案底部加入「llm 分析紀錄區」。僅變更註解，程式邏輯保持不變。
 */
